/*
 * Copyright (c) CIRROM 2021.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  approveSuggestedChange,
  getCRMURLForSuggestedChange,
  getExplainForSuggestedChange,
  transitionSuggestedChange,
} from "../../api/suggestedchanges";
import Button from "@mui/material/Button";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import IconButton from "@mui/material/IconButton";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import TableCell from "@mui/material/TableCell";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import Collapse from "@mui/material/Collapse";
import Alert, { AlertColor } from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import DomainIcon from "@mui/icons-material/Domain";
import PublicIcon from "@mui/icons-material/Public";
import FolderIcon from "@mui/icons-material/Folder";
import Tooltip from "@mui/material/Tooltip";
import GppGoodIcon from "@mui/icons-material/GppGood";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import GppBadIcon from "@mui/icons-material/GppBad";
import Zoom from "@mui/material/Zoom";
import LinearProgress from "@mui/material/LinearProgress";
import NavigateNext from "@mui/icons-material/NavigateNext";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import { TableRowForSuggestedChange } from "./TableRowForSuggestedChange";
import { describeChangeCount } from "./utils";
import { encodeValue, toReadableText } from "../utils/utils";
import { buildLinkedInSearchUrl } from "../../utils/linkedinUtils";

import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import {
  FeedbackOutlined,
  Report,
  Send,
  Share,
  ContentCopy,
} from "@mui/icons-material";
import {
  CirromMonitorCRMComparison,
  getMonitorCRMComparison,
} from "../../api/cirrommonitor";
import { askCopilot } from "../../api/llms";
import { MonitorComparisonTable } from "../cirrom-monitor/MonitorComparisonTable";
import Link from "@mui/material/Link";
import {
  UserProfileContext,
  SideBarWidthContext,
  ViewPortContext,
} from "../../App";
import TextField from "@mui/material/TextField";
import { submitFeedback } from "../../api/useractions";
import { CirromLogoAvatar } from "../Logo";
import { toPng } from "html-to-image";
import { isEmpty } from "lodash";
import { ModalLoadingCard } from "../utils/ModalLoadingCard";
import { ChickenOutModal } from "../utils/ChickenOutModal";
import { useAppliedChanges } from "../record-review/helpers/useAppliedChanges";
import { useCrmVendor } from "../record-review/helpers/useCrmVendor";
import { AppliedChangesList } from "../record-review/AppliedChangesList";
import classes from "./css/recordDetails.module.css";

/*
 * Filled action colour, for the one committing control on a surface.
 *
 * Amber is rationed: it marks the shell, the primary action and selection, and
 * nothing else (DESIGN.md, the rationed amber rule). Inside this modal that leaves
 * exactly two filled uses - Approve All, which writes to the CRM, and the Co-Pilot
 * send button, which is the only action in its own panel. Everything else that used
 * to be amber here was a utility or a role marker and now carries neutral chrome,
 * so the fill still means "this one commits".
 *
 * The text colour is computed from the actual fill rather than read from
 * palette.contrastText: the outgoing /app theme declares primary.contrastText as
 * white over an amber primary, which is unreadable. getContrastText picks from the
 * resolved fill, so this stays correct in every variant and inverts in dark.
 */
const primaryFilledSx = (theme) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.getContrastText(theme.palette.primary.main),
});

/*
 * Share / copy / feedback.
 *
 * These three get a record out of Cirrom; none of them writes to a system of record,
 * so none of them earns the action colour. They previously carried the same amber
 * fill as Approve All, which left three utilities shouting as loudly as the only
 * button on the surface with consequences.
 *
 * Neutral chrome on a divider hairline keeps them legible as one group and keeps
 * them findable, while ceding the surface's one loud colour to the commit action.
 */
const utilityIconSx = {
  color: "text.secondary",
  borderColor: "divider",
  "&:hover": {
    color: "text.primary",
    borderColor: "text.secondary",
    backgroundColor: "action.hover",
  },
};

/**
 * Represents a component that allows the user to provide feedback and submit it.
 * @param {Object} props - The properties for the component.
 * @param {Function} props.onClose - The callback function to be called when the component is closed.
 * @returns {JSX.Element} - The JSX element representing the component.
 */
const RecordDetailsFeedback = (props: { onClose: () => void }) => {
  const [feedbackText, setFeedbackText] = useState<string>("");

  return (
    <Box className={classes.feedbackPanel} sx={{ borderColor: "divider" }}>
      <TextField
        fullWidth
        multiline
        rows={2}
        placeholder={
          "NOTE: No PII data will be included with your submission - please think carefully what data you choose to share."
        }
        inputProps={{ className: classes.feedbackInput }}
        value={feedbackText}
        onChange={(event) => setFeedbackText(event.target.value)}
      />
      <div className={classes.feedbackActions}>
        <IconButton
          size="small"
          onClick={() => props.onClose()}
          color={"error"}
          sx={{ border: "1px solid", borderColor: "error.main" }}
        >
          <CloseIcon fontSize={"small"} />
        </IconButton>
        <IconButton
          size="small"
          color={"primary"}
          sx={{ border: "1px solid", borderColor: "primary.main" }}
          onClick={() =>
            submitFeedback(feedbackText).then((response) => {
              setFeedbackText("");
              props.onClose();
            })
          }
          disabled={!feedbackText}
        >
          <Send fontSize={"small"} />
        </IconButton>
      </div>
    </Box>
  );
};

/*
 * Email deliverability marker. The three variants differ only by icon and palette
 * entry, so they are looked up rather than branched three ways.
 *
 * Colours are palette paths, not literals, so they resolve through the theme and
 * stay legible in the dark variant - which is why they go to sx, not style.
 *
 * These three are the closest thing on this surface to a second traffic light
 * alongside the CRM health ramp. Left as-is on purpose: reconciling them with the
 * ramp is a colour decision, not a token one.
 */
const EMAIL_ACTIVITY = {
  success: { Icon: GppGoodIcon, color: "success.main" },
  warning: { Icon: GppMaybeIcon, color: "warning.main" },
  error: { Icon: GppBadIcon, color: "error.main" },
};

export const EmailTrafficLight = (props: {
  variant: string;
  status: string;
}) => {
  const activity = EMAIL_ACTIVITY[props.variant];
  if (!activity || !props.status) {
    return null;
  }
  const { Icon, color } = activity;
  return (
    <div className={classes.trafficLight}>
      <Icon className={classes.trafficLightIcon} sx={{ color }} />
      <Typography variant={"caption"}>{props.status}</Typography>
    </div>
  );
};

interface CopilotMessage {
  role: "user" | "assistant";
  content: string;
}

const CopilotPanel = (props: {
  integratedSystemId: number;
  recordId?: number;
  suggestedChangeId?: number;
  onClose: () => void;
}) => {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendQuery = (question?: string, predefinedQuestion?: string) => {
    if (isLoading) return;
    const displayText = question || predefinedQuestion;
    if (!displayText) return;

    setMessages((prev) => [...prev, { role: "user", content: displayText }]);
    setInput("");
    setIsLoading(true);

    askCopilot({
      integrated_system_id: props.integratedSystemId,
      record_id: props.recordId,
      suggested_change_id: props.suggestedChangeId,
      question: question || undefined,
      predefined_question: predefinedQuestion || undefined,
    })
      .then((response) => {
        if (!mountedRef.current) return;
        const answer =
          response?.answer ||
          response?.raw_query ||
          (response?.records?.length
            ? `Found ${response.records.length} record(s).`
            : "No results found.");
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: answer },
        ]);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Sorry, I was unable to process that request.",
          },
        ]);
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
  };

  const handleSend = () => {
    if (!input.trim()) return;
    sendQuery(input.trim());
  };

  return (
    <Card className={classes.copilotCard}>
      <CardHeader
        title="Co-Pilot"
        titleTypographyProps={{ variant: "h5" }}
        avatar={
          <img
            src="/copilot.png"
            alt="copilot"
            className={classes.copilotAvatar}
          />
        }
        action={
          <IconButton
            onClick={props.onClose}
            size="small"
            aria-label="Close Co-Pilot"
          >
            <CloseOutlinedIcon />
          </IconButton>
        }
        className={classes.copilotHeader}
      />
      <Divider />
      <Container className={classes.scrollPane}>
        <div className={classes.copilotMessages}>
          {messages.length === 0 && (
            <div className={classes.copilotEmpty}>
              <img
                src="/copilot.png"
                alt="copilot"
                className={classes.copilotEmptyLogo}
              />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Ask your Co-Pilot about this record
              </Typography>
            </div>
          )}
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={
                msg.role === "user"
                  ? classes.messageRowUser
                  : classes.messageRowAssistant
              }
            >
              {/*
               * Alignment already says who spoke, so the second cue is fill versus
               * hairline rather than a colour: the steward's own turn is filled, the
               * Co-Pilot's is outlined. Amber is not spent here - a role marker is a
               * data colour, and the rationed amber rule keeps it for the commit
               * action. Both tones resolve through the theme and invert in dark.
               */}
              <Box
                className={classes.messageBubble}
                sx={
                  msg.role === "user"
                    ? { bgcolor: "action.selected", color: "text.primary" }
                    : {
                        border: "1px solid",
                        borderColor: "divider",
                        color: "text.primary",
                      }
                }
              >
                <Typography variant="body2">{msg.content}</Typography>
              </Box>
            </div>
          ))}
          {isLoading && (
            <div className={classes.messageRowAssistant}>
              <Box
                className={classes.messageBubble}
                sx={{ border: "1px solid", borderColor: "divider" }}
              >
                <LinearProgress className={classes.copilotProgress} />
              </Box>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </Container>

      <Divider />
      <div className={classes.copilotPills}>
        {[
          { label: "Track whereabouts", prompt: "Track whereabouts" },
          { label: "Find internal data", prompt: "Find internal data" },
        ].map((pill) => (
          <Button
            key={pill.label}
            size="small"
            variant="outlined"
            disabled={isLoading}
            onClick={() => {
              sendQuery(undefined, pill.prompt);
            }}
            className={classes.copilotPill}
            sx={{
              color: "text.primary",
              borderColor: "divider",
              "&:hover": {
                borderColor: "text.secondary",
                backgroundColor: "action.hover",
              },
            }}
          >
            {pill.label}
          </Button>
        ))}
      </div>
      <div className={classes.copilotInputRow}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Ask a question..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          color="primary"
          size="small"
          aria-label="Send question to Co-Pilot"
          sx={input.trim() ? primaryFilledSx : undefined}
        >
          <Send fontSize="small" />
        </IconButton>
      </div>
    </Card>
  );
};

/**
 * SuggestedChangeDetailsModal Component
 *
 * This component displays the details of a suggested change in a modal dialog.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Determines if the modal is open or closed.
 * @param {function} [props.onClose] - Callback function to handle modal closure.
 * @param {RowNode|any} props.selectedNode - Selected row node or data.
 * @param {function} props.onSuccess - Callback function when a change is successfully approved.
 * @param {function} [props.onNextRowRequested] - Callback function to navigate to the next row.
 * @returns {JSX.Element} - SuggestedChangeDetailsModal component JSX element.
 */
export function SuggestedChangeDetailsModal(
  props: Readonly<{
    open: boolean;
    onClose?: () => void;
    onSuccess: (id?) => void;
    onPreviousRowRequested?: () => void;
    onNextRowRequested?: () => void;
    suggestedChangeId?: number;
    recordId?: number;
    batchId: number;
    integratedSystemId?: number;
  }>
) {
  const {
    suggestedChangeId,
    batchId,
    recordId,
    onPreviousRowRequested,
    onNextRowRequested,
  } = props;
  // State management
  const [entityType, setEntityType] = useState<string>("");
  const [suggestedExplain, setSuggestedExplain] = useState<any>(undefined);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertText, setAlertText] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");
  const [sourceFolderName, setSourceFolderName] = useState<string>(undefined);
  const [contactUrl, setContactUrl] = useState<string>("");
  const [accountUrl, setAccountUrl] = useState<string>("");
  const [companyUrl, setCompanyUrl] = useState<string>(undefined);
  const [emailStatuses, setEmailStatuses] = useState<string[][]>([]);
  const [requestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [duplicateGroupId, setDuplicateGroupId] = useState<number>(null);
  const [integratedSystemId, setIntegratedSystemId] = useState<number>(
    props.integratedSystemId
  );

  const theme = useTheme();
  const viewPortProps = useContext(ViewPortContext);
  const sideBarContext = useContext(SideBarWidthContext);
  const feedbackContext = useContext(UserProfileContext);

  const [isSideViewOpen, setIsSideViewOpen] = useState<boolean>(false);
  const [reduceContainerMargin, setReduceContainerMargin] =
    useState<boolean>(false);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [monitorData, setMonitorData] =
    useState<CirromMonitorCRMComparison>(undefined);
  const [monitorRequestInProgress, setMonitorRequestInProgress] =
    useState<boolean>(false);

  const [changeRecordId, setChangeRecordId] = useState<number>(null);
  /*
   * The record's CRM id, for the applied history. Not record_id: that changes
   * every batch, so it cannot reach changes applied in earlier ones.
   */
  const [crmId, setCrmId] = useState<string>(null);
  const [appliedRefreshKey, setAppliedRefreshKey] = useState<number>(0);
  const [changeIntegratedSystemId, setChangeIntegratedSystemId] =
    useState<number>(null);

  /*
   * Shown only once it answers, and only with something in it: its latency is
   * unknown, and the Data Quality tab must never wait on it.
   */
  const appliedChanges = useAppliedChanges({
    integratedSystemId: changeIntegratedSystemId,
    entityType:
      entityType === "ACCOUNT" || entityType === "CONTACT" ? entityType : null,
    crmId,
    refreshKey: appliedRefreshKey,
  });
  const showAppliedTab = !isEmpty(appliedChanges);
  const showMonitorTab = !!monitorData && !isEmpty(monitorData.pending_results);
  const crmVendor = useCrmVendor(integratedSystemId);

  const [lastBrowserView, setLastBrowserView] = useState<string>("");
  const [isCopilotOpen, setIsCopilotOpen] = useState<boolean>(false);
  // Which bulk action is awaiting confirmation; null when nothing is pending.
  const [pendingBulkAction, setPendingBulkAction] = useState<
    "approve" | "reject" | null
  >(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const alertTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(
    () => () => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }
    },
    []
  );
  /**
   * generateEmailStatusMessage function
   * Based on the provided response, generate an ( alert_type, alert_message ) tuple
   * This will display banners at the top of the modal indicating the email marketing activity
   * for the given contact
   * @param response
   */

  const generateEmailStatusMessage = (response) => {
    const emailStatuses = [];
    //success messages
    if (
      response?.email_data?.last_email_delivered ||
      response?.email_data?.last_email_open
    ) {
      if (response?.email_data?.last_email_open) {
        emailStatuses.push([
          "success",
          `Last opened email on ${new Date(
            Date.parse(response.email_data.last_email_open)
          ).toDateString()}.`,
        ]);
      } else if (response?.email_data?.last_email_delivered) {
        emailStatuses.push([
          "success",
          `Last delivered email on ${new Date(
            Date.parse(response.email_data.last_email_delivered)
          ).toDateString()}.`,
        ]);
      }
    }
    //errors
    if (
      response?.email_data?.last_email_hard_bounce ||
      response?.email_data?.last_email_soft_bounce
    ) {
      emailStatuses.push([
        "error",
        `Email ${
          response?.email_data?.last_email_soft_bounce ? "soft" : ""
        } bounce event on ${new Date(
          Date.parse(
            response?.email_data?.last_email_hard_bounce ||
              response?.email_data?.last_email_soft_bounce
          )
        ).toDateString()}.`,
      ]);
    }
    //never emailed
    if (emailStatuses.length === 0) {
      emailStatuses.push(["warning", "This contact has never been emailed."]);
    }
    setEmailStatuses(emailStatuses);
  };

  /**
   * fetchSuggestedChangeExplain function
   *
   * Retrieve the data required to populate the modal
   */
  const fetchSuggestedChangeExplain = () => {
    getExplainForSuggestedChange(
      suggestedChangeId,
      batchId,
      recordId,
      props.integratedSystemId
    )
      .then((response) => {
        let formattedResponse = {};
        response.contact_data.forEach((item) => {
          formattedResponse[item.field_name] = item;
        });

        generateEmailStatusMessage(response);
        setSourceFolderName(response.source_folder_name);
        setCompanyUrl(response.company_website);
        setDuplicateGroupId(response.duplicate_group_id);
        setIntegratedSystemId(response.integrated_system_id);

        setSuggestedExplain(formattedResponse);
        setChangeRecordId(response.record_id);
        setChangeIntegratedSystemId(response.integrated_system_id);
        setEntityType(response.entity_type);
        setCrmId(
          (response.entity_type === "ACCOUNT"
            ? response.crm_account_id
            : response.crm_contact_id) ?? null
        );
        // Every explain refetch follows a write, so the history follows too.
        setAppliedRefreshKey((key) => key + 1);
        const changeIds = new Set<number>();
        Object.keys(formattedResponse).forEach((item) => {
          formattedResponse[item].cirrom_changes.forEach((changeItem) => {
            changeIds.add(changeItem.suggested_change_id);
          });
        });
        getCRMURLForSuggestedChange(changeIds.values().next().value)
          .then((urlResponse) => {
            setContactUrl(urlResponse?.contact);
            setAccountUrl(urlResponse?.account);
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => console.log(err));
  };

  /**
   * Display an alert on request complete
   *
   * Success dismisses itself; warnings and errors stay until the steward closes
   * them. A partial or failed CRM write is the one thing on this surface they must
   * not miss, and a banner that disappears after 2.5s is exactly how they would.
   *
   * The pending timer is tracked so a second alert cannot inherit the first one's
   * countdown and vanish early.
   *
   * @param textToDisplay string
   * @param alertVariant success|error|warning
   */
  const displayAlert = (textToDisplay: string, alertVariant: string) => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = undefined;
    }
    setAlertSeverity(alertVariant as AlertColor);
    setAlertText(textToDisplay);
    setAlertOpen(true);
    if (alertVariant === "success") {
      alertTimerRef.current = setTimeout(() => setAlertOpen(false), 2500);
    }
  };

  //reset state and fetch new data from the server
  useEffect(() => {
    setSuggestedExplain(undefined);
    setEntityType(undefined);
    setAlertOpen(false);
    setContactUrl("");
    setAccountUrl("");
    setCompanyUrl("");
    setIsFeedbackOpen(false);
    setRequestInProgress(false);
    setActiveTab(0);
    setMonitorData(undefined);
    setMonitorRequestInProgress(false);
    setIsCopilotOpen(false);
    setCrmId(null);
    let cancelled = false;
    if (suggestedChangeId || recordId) {
      fetchSuggestedChangeExplain();
      if (recordId && props.integratedSystemId) {
        setMonitorRequestInProgress(true);
        getMonitorCRMComparison(recordId, props.integratedSystemId)
          .then((resp) => {
            if (!cancelled) setMonitorData(resp);
          })
          .catch(() => {
            if (!cancelled) setMonitorData(undefined);
          })
          .finally(() => {
            if (!cancelled) setMonitorRequestInProgress(false);
          });
      }
    }
    return () => {
      cancelled = true;
    };
  }, [suggestedChangeId, recordId, props.integratedSystemId]);

  useEffect(() => {
    let cancelled = false;
    if (changeRecordId) {
      if (changeIntegratedSystemId) {
        getMonitorCRMComparison(changeRecordId, changeIntegratedSystemId)
          .then((resp) => {
            if (!cancelled) setMonitorData(resp);
          })
          .catch(() => {
            if (!cancelled) setMonitorData(undefined);
          })
          .finally(() => {
            if (!cancelled) setMonitorRequestInProgress(false);
          });
      }
    }
    return () => {
      cancelled = true;
    };
  }, [changeRecordId, recordId, changeIntegratedSystemId]);

  useEffect(() => {
    if (
      lastBrowserView &&
      suggestedExplain &&
      //@ts-ignore
      window?.electronAPI?.openSideView
    ) {
      setIsSideViewOpen(true);
      //@ts-ignore
      window.electronAPI
        //@ts-ignore
        .openSideView(getContactSearchURL(lastBrowserView === "google"));
      //@ts-ignore
      if (window?.electronAPI?.onMessage) {
        //@ts-ignore
        window?.electronAPI?.onMessage(() => {
          setIsSideViewOpen(false);
          setLastBrowserView("");
        });
      }
    }
  }, [suggestedExplain, lastBrowserView]);

  /**
   * getActionableChangeIds
   * The changes a bulk action would actually touch. Computed before the
   * confirmation opens so the dialog can state a real number rather than "all",
   * and reused by the action itself so the two can never disagree.
   *
   * @param negativeTransition - when true, count the changes a reject would touch
   */
  const getActionableChangeIds = (negativeTransition: boolean): number[] => {
    const changeIds = new Set<number>();
    Object.keys(suggestedExplain ?? {}).forEach((item) => {
      suggestedExplain[item].cirrom_changes.forEach((changeItem) => {
        if (
          changeItem.status === "Active" &&
          (negativeTransition || changeItem.cirrom_value)
        ) {
          changeIds.add(changeItem.suggested_change_id);
        }
      });
    });
    return Array.from(changeIds);
  };

  /**
   * A settled bulk call fails two ways: the request itself rejected, or it resolved
   * carrying a `failures` map. The per-field approve button already reads that
   * second case, and a bulk run that ignored it would report success over changes
   * the CRM refused.
   */
  const isSettledSuccess = (result: PromiseSettledResult<any>) =>
    result.status === "fulfilled" && isEmpty(result.value?.failures);

  /**
   * requestBulkAction
   * Opens the confirmation. Nothing reaches the CRM until it is answered.
   */
  const requestBulkAction = (negativeTransition: boolean) => {
    const changeIds = getActionableChangeIds(negativeTransition);
    if (!changeIds.length) {
      displayAlert(
        negativeTransition
          ? "No Cirrom changes left to reject on this record."
          : "No approvable Cirrom changes found.",
        "warning"
      );
      return;
    }
    setPendingBulkAction(negativeTransition ? "reject" : "approve");
  };

  /**
   * approveAllSuggestedChanges
   * Apply every actionable change on this record. Uses allSettled rather than all,
   * so one refused write cannot hide the outcome of the others - a steward has to
   * be able to tell a clean run from a partial one.
   *
   * @param negativeTransition - when true, it rejects the changes
   */
  const approveAllSuggestedChanges = (negativeTransition) => {
    const changeIds = getActionableChangeIds(negativeTransition);
    if (!changeIds.length) {
      displayAlert("No approvable Cirrom changes found.", "warning");
      return;
    }
    setRequestInProgress(true);

    const total = changeIds.length;
    const calls = negativeTransition
      ? changeIds.map((changeId) => transitionSuggestedChange([changeId], true))
      : changeIds.map((changeId) => approveSuggestedChange(changeId));

    Promise.allSettled(calls)
      .then((results) => {
        /*
         * Report each change that actually settled cleanly, by id.
         *
         * Both outcomes have to be reported: the parent grid lists changes that are
         * still pending a decision, and a rejected change is no longer pending any
         * more than an approved one is. Rejecting used to leave its rows sitting in
         * the queue.
         *
         * Per id rather than all-or-nothing, so a partial run drops exactly the rows
         * that were actioned and leaves the ones that still need a decision.
         */
        const succeededIds = changeIds.filter((_, index) =>
          isSettledSuccess(results[index])
        );
        const failed = total - succeededIds.length;
        succeededIds.forEach((id) => props.onSuccess(id));
        if (failed === 0) {
          displayAlert(
            negativeTransition
              ? `${describeChangeCount(
                  total
                )} rejected. Cirrom won't suggest them again for this record.`
              : `${describeChangeCount(total)} written to your CRM.`,
            "success"
          );
        } else if (failed === total) {
          displayAlert(
            negativeTransition
              ? `None of the ${total} changes could be rejected. Nothing has changed - try again.`
              : `None of the ${total} changes reached your CRM. Nothing has changed - try again.`,
            "error"
          );
        } else {
          // The dangerous case: the record is now part-actioned. Name the split.
          displayAlert(
            negativeTransition
              ? `${
                  total - failed
                } of ${total} changes rejected. ${failed} could not be rejected and are still pending.`
              : `${
                  total - failed
                } of ${total} changes written to your CRM. ${failed} did not reach it and are still pending.`,
            "warning"
          );
        }
      })
      .catch((err) => {
        // allSettled does not reject, so this is a defect rather than a failed
        // write. Still surface it: a silent catch here is what left the progress
        // bar running forever.
        console.error("Bulk change action failed unexpectedly", err);
        displayAlert(
          "Something went wrong applying these changes. Nothing is confirmed - reopen the record to check.",
          "error"
        );
      })
      .finally(() => {
        setRequestInProgress(false);
        fetchSuggestedChangeExplain();
      });
  };

  /**
   * canApproveOrReject
   * Check if the suggested change is active
   */
  const canApproveOrReject = () => {
    let found = false;
    Object.keys(suggestedExplain).forEach((item) => {
      suggestedExplain[item].cirrom_changes.forEach((changeItem) => {
        if (changeItem.status === "Active") {
          found = true;
        }
      });
    });
    return found;
  };

  /**
   * getContactSearchURL
   * get google/linkedin search urls
   * @param isGoogle
   */
  const getContactSearchURL = (isGoogle?: boolean) => {
    let firstName = suggestedExplain?.first_name?.crm_value;
    let lastName = suggestedExplain?.last_name?.crm_value;
    let company = suggestedExplain?.company?.crm_value;
    if (
      suggestedExplain?.first_name?.cirrom_changes &&
      suggestedExplain.first_name.cirrom_changes.length > 0
    ) {
      firstName = suggestedExplain.first_name.cirrom_changes[0].cirrom_value;
    }
    if (
      suggestedExplain?.last_name?.cirrom_changes &&
      suggestedExplain.last_name.cirrom_changes.length > 0
    ) {
      lastName = suggestedExplain.last_name.cirrom_changes[0].cirrom_value;
    }

    if (isGoogle) {
      const encodedFirstName = encodeValue(firstName);
      const encodedLastName = encodeValue(lastName);
      const encodedCompany = encodeValue(company);
      return `https://www.google.com/search?q=${encodedFirstName} ${encodedLastName} ${encodedCompany}`;
    }
    return buildLinkedInSearchUrl({ firstName, lastName, company });
  };

  /**
   * getFieldNameOrCirromValue
   * if no crm value for field, return the cirrom change
   * @param field
   */
  const getFieldNameOrCirromValue = (field) => {
    if (field?.crm_value) {
      return field.crm_value;
    }
    if (field?.cirrom_changes.length > 0) {
      return field.cirrom_changes[0].cirrom_value;
    }
    return "";
  };

  const canSearchContact =
    entityType === "CONTACT" &&
    Boolean(
      getFieldNameOrCirromValue(suggestedExplain?.first_name) &&
        getFieldNameOrCirromValue(suggestedExplain?.last_name)
    );

  /**
   * Fetch the rows to be populated in the grid, order is important
   */
  const getRecordRows = () => {
    const companyLevelRows = [
      [
        "Telephone",
        suggestedExplain.telephone,
        suggestedExplain?.phone_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.phone_owner_id?.crm_value,
      ],
      [
        "Street",
        suggestedExplain.street,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
      [
        "City",
        suggestedExplain.city,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
      [
        "State",
        suggestedExplain.stateorprovince,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
      [
        "Zip Code",
        suggestedExplain.zipcode,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
      [
        "Country",
        suggestedExplain.country,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
    ];

    if (entityType === "CONTACT") {
      let companyValues = suggestedExplain.company;
      if (
        !suggestedExplain?.company?.crm_value &&
        suggestedExplain?.text_company.crm_value
      ) {
        companyValues = {
          ...suggestedExplain.company,
          isTextCompany: true,
          textCompany: { ...suggestedExplain.text_company },
        };
      }
      return [
        ["Salutation", suggestedExplain.salutation, false],
        ["First Name", suggestedExplain.first_name, false],
        ["Middle Name", suggestedExplain.middle_name, false],
        ["Last Name", suggestedExplain.last_name, false],
        ["Goes By", suggestedExplain.goes_by, false],
        ["Suffix", suggestedExplain.suffix, false],
        ["Company", companyValues, false],
        ["Job Title", suggestedExplain.job_title, false],
        ["Email", suggestedExplain.primary_email, false],
        ["Address Linkage", suggestedExplain.address_linkage, false],

        ...companyLevelRows,
      ];
    } else {
      return [
        ["Company", suggestedExplain.company, false],
        ...companyLevelRows,
      ];
    }
  };

  const openExternalLookup = (isGoogle) => {
    setLastBrowserView((isGoogle && "google") || "linkedin");
    //@ts-ignore
    if (window?.electronAPI?.openSideView) {
      //@ts-ignore
      window.electronAPI
        //@ts-ignore
        .openSideView(getContactSearchURL(isGoogle));
      //@ts-ignore
      if (window?.electronAPI?.onMessage) {
        //@ts-ignore
        window?.electronAPI?.onMessage(() => {
          setIsSideViewOpen(false);
          setLastBrowserView("");
        });
      }
    } else {
      window.open(getContactSearchURL(isGoogle), "_blank");
    }
  };

  /**
   * handleExportAsImage
   * Export the modal content as PNG image
   */
  const handleExportAsImage = useCallback(() => {
    if (cardRef.current === null) {
      return;
    }

    const contactName =
      entityType === "CONTACT"
        ? `${getFieldNameOrCirromValue(
            suggestedExplain.first_name
          )} ${getFieldNameOrCirromValue(suggestedExplain.last_name)}`
        : getFieldNameOrCirromValue(suggestedExplain.company);

    toPng(cardRef.current, {
      cacheBust: true,
      backgroundColor: theme.palette.background.default,
    })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = `${contactName
          .replaceAll(/[^a-z0-9]/gi, "_")
          .toLowerCase()}.png`;
        link.href = dataUrl;
        link.click();
        displayAlert("Contact record exported as image!", "success");
      })
      .catch((err) => {
        console.log(err);
        displayAlert("Failed to export image", "error");
      });
  }, [entityType, suggestedExplain, cardRef, theme]);

  /**
   * handleShareRecord
   * Generate and open pre-filled email with contact details and export as image
   */
  const handleShareRecord = () => {
    if (cardRef.current === null) {
      displayAlert("Unable to export image for sharing", "error");
      return;
    }

    const contactName =
      entityType === "CONTACT"
        ? `${getFieldNameOrCirromValue(
            suggestedExplain.first_name
          )} ${getFieldNameOrCirromValue(suggestedExplain.last_name)}`
        : getFieldNameOrCirromValue(suggestedExplain.company);

    const company =
      entityType === "CONTACT"
        ? getFieldNameOrCirromValue(suggestedExplain.company)
        : "";

    const jobTitle =
      entityType === "CONTACT"
        ? getFieldNameOrCirromValue(suggestedExplain.job_title)
        : "";

    const email =
      entityType === "CONTACT"
        ? getFieldNameOrCirromValue(suggestedExplain.primary_email)
        : "";

    const phone = getFieldNameOrCirromValue(suggestedExplain.telephone);

    // Export image to clipboard and then open email
    toPng(cardRef.current, {
      cacheBust: true,
      backgroundColor: theme.palette.background.default,
    })
      .then(async (dataUrl) => {
        try {
          // Convert data URL to blob and copy to clipboard
          const response = await fetch(dataUrl);
          const blob = await response.blob();

          await navigator.clipboard.write([
            new ClipboardItem({
              "image/png": blob,
            }),
          ]);

          // Construct email subject and body
          const subject = encodeURIComponent(`Cirrom Contact: ${contactName}`);

          let body = `Hi there,\n\nI wanted to share this contact record from Cirrom:\n\n`;
          body += `Name: ${contactName}\n`;

          if (company) {
            body += `Company: ${company}\n`;
          }

          if (jobTitle) {
            body += `Job Title: ${jobTitle}\n`;
          }

          if (email) {
            body += `Email: ${email}\n`;
          }

          if (phone) {
            body += `Phone: ${phone}\n`;
          }

          body += `\n--- Detailed Record Data ---\n\n`;
          body += `Please find the detailed contact record attached as an image. The image shows all CRM and Cirrom suggested data for easy reference.\n\n`;
          body += `Note: The contact record image has been copied to your clipboard. Please paste it as an attachment in your email.\n\n`;
          body += `Please let me know if you have any questions or need additional context.\n\n`;
          body += `Best regards`;

          const encodedBody = encodeURIComponent(body);
          const mailtoLink = `mailto:?subject=${subject}&body=${encodedBody}`;

          window.open(mailtoLink, "_blank");
          displayAlert(
            "Contact record copied to clipboard and email opened!",
            "success"
          );
        } catch (clipboardErr) {
          console.error("Failed to copy to clipboard:", clipboardErr);
          displayAlert(
            "Email opened, but failed to copy image to clipboard",
            "warning"
          );

          // Still open the email even if clipboard fails
          const subject = encodeURIComponent(`Cirrom Contact: ${contactName}`);
          let body = `Hi there,\n\nI wanted to share this contact record from Cirrom:\n\n`;
          body += `Name: ${contactName}\n`;
          if (company) body += `Company: ${company}\n`;
          if (jobTitle) body += `Job Title: ${jobTitle}\n`;
          if (email) body += `Email: ${email}\n`;
          if (phone) body += `Phone: ${phone}\n`;
          body += `\n--- Detailed Record Data ---\n\nPlease use the separate copy button to get the contact record image for attachment.\n\nBest regards`;

          const encodedBody = encodeURIComponent(body);
          window.open(
            `mailto:?subject=${subject}&body=${encodedBody}`,
            "_blank"
          );
        }
      })
      .catch((err) => {
        console.log(err);
        displayAlert("Failed to export image for sharing", "error");
      });
  };

  /**
   * handleCopyImageToClipboard
   * Copy contact record as image to clipboard
   */
  const handleCopyImageToClipboard = async () => {
    if (cardRef.current === null) {
      displayAlert("Unable to copy image to clipboard", "error");
      return;
    }

    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        backgroundColor: theme.palette.background.default,
      });

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": blob,
        }),
      ]);

      displayAlert("Contact record copied to clipboard as image!", "success");
    } catch (err) {
      console.error("Failed to copy image to clipboard:", err);
      displayAlert("Failed to copy image to clipboard", "error");
    }
  };

  useEffect(() => {
    //@ts-ignore
    if (window?.electronAPI?.openSideView) {
      if (isSideViewOpen) {
        setReduceContainerMargin(true);
        viewPortProps.minimizeView();
        sideBarContext.minimize();
      } else {
        setReduceContainerMargin(false);
        viewPortProps.maximizeView();
        sideBarContext.maximize();
      }
    }
  }, [isSideViewOpen]);

  return (
    <Modal
      open={props.open}
      onClose={() => {
        //@ts-ignore
        if (window?.electronAPI?.closeSideView) {
          setIsSideViewOpen(false);
          setLastBrowserView("");
          //@ts-ignore
          window.electronAPI.closeSideView();
        }
        props.onClose();
      }}
      className={classes.modalRoot}
    >
      <div
        className={
          isCopilotOpen ? classes.modalShellWithCopilot : classes.modalShell
        }
      >
        <Container
          maxWidth={suggestedExplain ? "md" : "sm"}
          className={
            reduceContainerMargin
              ? classes.recordContainerNarrow
              : classes.recordContainer
          }
        >
          {suggestedExplain ? (
            <Zoom in={true}>
              <Card ref={cardRef}>
                <CardHeader
                  className={classes.recordHeader}
                  title={
                    <div className={classes.recordTitle}>
                      {entityType === "CONTACT"
                        ? `${getFieldNameOrCirromValue(
                            suggestedExplain.first_name
                          )} ${getFieldNameOrCirromValue(
                            suggestedExplain.last_name
                          )}`
                        : `${getFieldNameOrCirromValue(
                            suggestedExplain.company
                          )}`}
                      <div className={classes.recordHeaderActions}>
                        {sourceFolderName && (
                          <>
                            <Tooltip title={sourceFolderName}>
                              <FolderIcon
                                className={classes.headerIcon}
                                sx={{ color: "secondary.main" }}
                              />
                            </Tooltip>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                          </>
                        )}

                        {contactUrl && (
                          <Tooltip title={"View Contact in CRM"}>
                            <LaunchIcon
                              className={classes.headerAction}
                              onClick={() => window.open(contactUrl, "_blank")}
                            />
                          </Tooltip>
                        )}

                        {accountUrl && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"View Account in CRM"}>
                              <DomainIcon
                                className={classes.headerAction}
                                onClick={() =>
                                  window.open(accountUrl, "_blank")
                                }
                              />
                            </Tooltip>
                          </>
                        )}

                        {companyUrl && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"Open Company Website"}>
                              <PublicIcon
                                className={classes.headerAction}
                                onClick={() =>
                                  window.open(companyUrl, "_blank")
                                }
                              />
                            </Tooltip>
                          </>
                        )}

                        {canSearchContact && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"Search on LinkedIn"}>
                              <img
                                src={"/linkedin-logo.svg"}
                                className={classes.headerLogo}
                                onClick={() => {
                                  openExternalLookup(false);
                                }}
                                onKeyDown={() =>
                                  window.open(getContactSearchURL(), "_blank")
                                }
                                alt={"linkedin-logo"}
                                role={"presentation"}
                              />
                            </Tooltip>
                          </>
                        )}
                        {canSearchContact && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"Search on Google"}>
                              <img
                                src={"/google-logo.png"}
                                className={classes.headerLogo}
                                onKeyDown={() =>
                                  window.open(
                                    getContactSearchURL(true),
                                    "_blank"
                                  )
                                }
                                onClick={() => {
                                  openExternalLookup(true);
                                }}
                                alt={"google-logo"}
                                role={"presentation"}
                              />
                            </Tooltip>
                          </>
                        )}

                        {!!integratedSystemId && feedbackContext.canUseCopilot && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"Ask Co-Pilot"}>
                              <Box
                                component="img"
                                src={"/copilot.png"}
                                className={classes.headerLogoToggle}
                                sx={{
                                  borderColor: isCopilotOpen
                                    ? "primary.main"
                                    : "transparent",
                                }}
                                onClick={() =>
                                  setIsCopilotOpen((prev) => !prev)
                                }
                                alt={"copilot"}
                                role={"presentation"}
                              />
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </div>
                  }
                  titleTypographyProps={{ variant: "h3" }}
                  subheader={
                    <div>
                      {entityType === "CONTACT" && (
                        <>
                          <div>
                            {suggestedExplain?.contact_source?.crm_value &&
                              ` Sourced from: ${toReadableText(
                                suggestedExplain.contact_source.crm_value
                              )}`}
                            {suggestedExplain?.contact_source?.crm_value &&
                              suggestedExplain?.job_title?.crm_value &&
                              " | "}
                            {`${suggestedExplain?.job_title?.crm_value || ""}`}
                          </div>
                          <div className={classes.trafficLightRow}>
                            {emailStatuses.map((status) => (
                              <EmailTrafficLight
                                variant={status[0]}
                                status={status[1]}
                                key={`${status[0]}-${status[1]}`}
                              />
                            ))}
                          </div>

                          {duplicateGroupId !== null &&
                            integratedSystemId !== null && (
                              <div className={classes.duplicateNotice}>
                                <Report
                                  className={classes.duplicateNoticeIcon}
                                  sx={{ color: "error.main" }}
                                  fontSize={"small"}
                                />
                                <Link
                                  underline={"always"}
                                  color={"text.secondary"}
                                  className={classes.duplicateNoticeLink}
                                  onClick={() =>
                                    window.open(
                                      window.location.origin +
                                        `/app/duplicates/${integratedSystemId}?duplicateGroupId=${duplicateGroupId}`
                                    )
                                  }
                                >
                                  <Typography variant={"caption"}>
                                    This contact has a duplicate record
                                    associated.
                                  </Typography>
                                </Link>
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  }
                  avatar={
                    <CirromLogoAvatar
                      styleOverrides={{
                        width: "64px",
                        height: "64px",
                      }}
                    />
                  }
                  action={
                    <div className={classes.recordHeaderNav}>
                      <IconButton
                        onClick={() => {
                          //@ts-ignore
                          if (window?.electronAPI?.closeSideView) {
                            setIsSideViewOpen(false);
                            setLastBrowserView("");
                            //@ts-ignore
                            window.electronAPI.closeSideView();
                          }
                          props.onClose();
                        }}
                        color={"error"}
                      >
                        <CloseOutlinedIcon />
                      </IconButton>
                      {(onPreviousRowRequested || onNextRowRequested) && (
                        <div className={classes.recordNavButtons}>
                          <IconButton
                            onClick={() => onPreviousRowRequested?.()}
                            color={"info"}
                            disabled={!onPreviousRowRequested}
                            aria-label="Previous contact"
                            className={classes.recordNavButton}
                          >
                            <NavigateBefore />
                            <Typography variant={"caption"}>Prev</Typography>
                          </IconButton>
                          <IconButton
                            onClick={() => onNextRowRequested?.()}
                            color={"info"}
                            disabled={!onNextRowRequested}
                            aria-label="Next contact"
                            className={classes.recordNavButton}
                          >
                            <NavigateNext />
                            <Typography variant={"caption"}>Next</Typography>
                          </IconButton>
                        </div>
                      )}
                    </div>
                  }
                />
                <Collapse in={alertOpen}>
                  <Alert
                    severity={alertSeverity}
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => {
                          setAlertOpen(false);
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    sx={{ mb: 2 }}
                  >
                    {alertText}
                  </Alert>
                </Collapse>
                {requestInProgress && <LinearProgress />}

                <Divider />
                {(showMonitorTab || showAppliedTab) && (
                  <>
                    <Tabs
                      value={activeTab}
                      onChange={(_e, newValue) => setActiveTab(newValue)}
                      sx={{
                        px: 2,
                        "& .MuiTab-root.Mui-selected": {
                          color: "text.primary",
                          fontWeight: 600,
                        },
                      }}
                    >
                      <Tab label="Data Quality" value={0} />
                      {showMonitorTab && <Tab label="Monitor" value={1} />}
                      {showAppliedTab && (
                        <Tab
                          value={2}
                          aria-label={`Applied changes, ${appliedChanges.length}`}
                          label={
                            <span>
                              Applied changes
                              <Box
                                component="span"
                                className={classes.tabCount}
                                sx={{
                                  borderColor: "divider",
                                  color: "text.secondary",
                                }}
                              >
                                {appliedChanges.length}
                              </Box>
                            </span>
                          }
                        />
                      )}
                    </Tabs>
                    <Divider />
                  </>
                )}
                <Container className={classes.scrollPane}>
                  {activeTab === 0 && (
                    <TableContainer>
                      <Table size={"small"}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <span />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant={"h5"}
                                className={classes.comparisonColumnHeader}
                              >
                                CRM
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant={"h5"}
                                className={classes.comparisonColumnHeader}
                              >
                                CIRROM
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getRecordRows().map((entry) => {
                            return (
                              <TableRowForSuggestedChange
                                key={entry[0]}
                                fieldName={entry[0]}
                                fieldFromAccount={entry[2]}
                                integratedSystemId={integratedSystemId}
                                suggestedExplainEntry={entry[1]}
                                onSuccess={(suggestedId?) => {
                                  fetchSuggestedChangeExplain();
                                  props.onSuccess(suggestedId);
                                  setRequestInProgress(false);
                                }}
                                displayAlert={displayAlert}
                                suggestedChangeId={suggestedChangeId}
                                onActionInitiated={() => {
                                  setRequestInProgress(true);
                                }}
                                /*
                                 * A per-field action that fails has to release the
                                 * in-flight flag. It gates the bulk buttons, so a
                                 * silent failure used to leave them disabled until
                                 * the modal was reopened - and told the steward
                                 * nothing about the change that did not land.
                                 */
                                onActionFailed={(message) => {
                                  setRequestInProgress(false);
                                  displayAlert(message, "error");
                                }}
                              />
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {activeTab === 1 &&
                    monitorData &&
                    !isEmpty(monitorData.pending_results) && (
                      <MonitorComparisonTable
                        suggestedExplain={monitorData}
                        recordId={recordId || changeRecordId}
                        integratedSystemId={integratedSystemId}
                        requestInProgress={monitorRequestInProgress}
                        setRequestInProgress={setMonitorRequestInProgress}
                        onSuccess={(id?) => {
                          props.onSuccess(id);
                          if (
                            (recordId || changeRecordId) &&
                            props.integratedSystemId
                          ) {
                            setMonitorRequestInProgress(true);
                            getMonitorCRMComparison(
                              recordId || changeRecordId,
                              props.integratedSystemId
                            )
                              .then((resp) => setMonitorData(resp))
                              .catch(() => setMonitorData(undefined))
                              .finally(() =>
                                setMonitorRequestInProgress(false)
                              );
                          }
                        }}
                        displayAlert={displayAlert}
                      />
                    )}
                  {activeTab === 1 && monitorRequestInProgress && (
                    <LinearProgress />
                  )}
                  {activeTab === 2 && showAppliedTab && (
                    <AppliedChangesList
                      changes={appliedChanges}
                      crmLabel={crmVendor?.label}
                    />
                  )}
                </Container>

                <Container className={classes.recordFooter}>
                  {activeTab === 0 && (
                    <>
                      <div className={classes.recordFooterUtilities}>
                        <Tooltip title="Share contact details via email with image export">
                          <IconButton
                            onClick={handleShareRecord}
                            color={"info"}
                            aria-label="Share contact details via email"
                            className={classes.utilityButton}
                            sx={utilityIconSx}
                          >
                            <Share
                              fontSize={"small"}
                              className={classes.utilityButtonIcon}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy contact record image to clipboard">
                          <IconButton
                            onClick={handleCopyImageToClipboard}
                            color={"info"}
                            aria-label="Copy contact record image to clipboard"
                            className={classes.utilityButton}
                            sx={utilityIconSx}
                          >
                            <ContentCopy
                              fontSize={"small"}
                              className={classes.utilityButtonIcon}
                            />
                          </IconButton>
                        </Tooltip>
                        {!isFeedbackOpen && feedbackContext.canSupplyFeedback && (
                          <Tooltip
                            title={
                              <>
                                <Typography variant={"caption"}>
                                  Send feedback to Cirrom about this suggestion
                                </Typography>
                                <br />
                                <Typography variant={"caption"}>
                                  NOTE: No PII data will be included with your
                                  submission - please think carefully what data
                                  you choose to share.
                                </Typography>
                              </>
                            }
                          >
                            <IconButton
                              onClick={() => setIsFeedbackOpen(true)}
                              color={"info"}
                              aria-label="Send feedback about this suggestion"
                              className={classes.utilityButton}
                              sx={utilityIconSx}
                            >
                              <FeedbackOutlined
                                fontSize={"small"}
                                className={classes.utilityButtonIcon}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>

                      {canApproveOrReject() && (
                        <div className={classes.recordCommitActions}>
                          <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            disabled={requestInProgress}
                            onClick={() => requestBulkAction(false)}
                          >
                            Approve All
                          </Button>
                          {/*
                           * Outlined, so Approve All is the only filled button on
                           * the surface and the one that writes to the CRM is the
                           * one the eye lands on.
                           *
                           * Neutral rather than secondary or error. Secondary is
                           * orange[800] in the CIRROM variant, which would put a
                           * near-amber button beside the amber one, and it reads
                           * at 3.1:1. Error red would frame this as a mistake -
                           * rejecting a bad suggestion is a steward doing the job,
                           * not an accident. A firmer border than the utility
                           * icons opposite keeps it a peer of Approve All rather
                           * than a third utility.
                           */}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={requestInProgress}
                            onClick={() => requestBulkAction(true)}
                            sx={{
                              color: "text.primary",
                              borderColor: "text.secondary",
                              "&:hover": {
                                borderColor: "text.primary",
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            Reject All
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </Container>
                {isFeedbackOpen && (
                  <RecordDetailsFeedback
                    onClose={() => setIsFeedbackOpen(false)}
                  />
                )}
              </Card>
            </Zoom>
          ) : (
            <ModalLoadingCard onClose={() => props.onClose()} />
          )}
        </Container>
        {isCopilotOpen &&
          feedbackContext.canUseCopilot &&
          !!suggestedExplain &&
          !!integratedSystemId &&
          !!(recordId || changeRecordId || suggestedChangeId) && (
            <CopilotPanel
              integratedSystemId={integratedSystemId}
              recordId={recordId || changeRecordId}
              suggestedChangeId={suggestedChangeId}
              onClose={() => setIsCopilotOpen(false)}
            />
          )}
        {/*
         * Both bulk actions confirm before anything leaves the browser. Approve
         * writes to the customer's system of record; reject is durable, so the
         * copy borrows the per-field wording a steward already knows ("Cirrom
         * won't suggest it in the future") rather than inventing a second phrasing.
         *
         * The count comes from the same helper the action uses, so the number in
         * the dialog is the number that gets actioned.
         */}
        <ChickenOutModal
          modalOpen={pendingBulkAction !== null}
          title={
            pendingBulkAction === "reject"
              ? "Reject all changes?"
              : "Write all changes to your CRM?"
          }
          message={
            pendingBulkAction === "reject"
              ? `Reject ${describeChangeCount(
                  getActionableChangeIds(true).length
                )} on this record. Cirrom won't suggest them again for this record.`
              : `Write ${describeChangeCount(
                  getActionableChangeIds(false).length
                )} to your CRM. This updates the record other people work from.`
          }
          onModalOpenChange={(open) => {
            if (!open) {
              setPendingBulkAction(null);
            }
          }}
          onSuccess={() => {
            const negativeTransition = pendingBulkAction === "reject";
            setPendingBulkAction(null);
            approveAllSuggestedChanges(negativeTransition);
          }}
        />
      </div>
    </Modal>
  );
}
